import { FlightCard, PromoData, Sectors } from '@/src/types/flight'
import { CouponDetails, ExtractedFlightCardProps } from './flightCardProps'

// Utility to extract layovers for a flight
export function getLayoversForFlight (
    flight: FlightCard,
    sectors: Sectors
): Array<{ durationMins: number; durationStr: string; city: string }> {
    if (!flight.sectorKeys || !sectors) return []
    const layovers: Array<{ durationMins: number; durationStr: string; city: string }> = []
    flight.sectorKeys.forEach((key: string) => {
        const sector = sectors[key]
        if (!sector || !sector.flights || !sector.flights.segments) return
        const { segments } = sector.flights
        const { layoverDurationMap = [] } = sector
            ; (layoverDurationMap as number[]).forEach((layoverMins: number, idx: number) => {
                if (!segments[idx] || !segments[idx + 1]) return
                const city = segments[idx].arrival?.airportName?.c || segments[idx].arrival?.airportCode || ''
                layovers.push({
                    durationMins: layoverMins,
                    durationStr: `${Math.floor(layoverMins / 60)}h ${layoverMins % 60}m`,
                    city
                })
            })
    })
    return layovers
}

export function extractFlightCardProps (
    flight: FlightCard,
    sectors: Sectors,
    getFlightData: (f: FlightCard) => GetFlightDataReturn,
    isPerPaxPricingEnabled: boolean
): ExtractedFlightCardProps {
    const flightData = getFlightData ? getFlightData(flight) : (flight as unknown as ExtractedFlightCardProps)
    return {
        airlineName: flightData.airlineName || '-',
        departureTime: flightData.departureTime || '-',
        arrivalTime: flightData.arrivalTime || '-',
        airlineCodes: flightData.airlineCodes || [],
        stops: typeof flightData.stops === 'number' ? flightData.stops : 0,
        totalDuration: typeof flightData.totalDuration === 'number' ? flightData.totalDuration : 0,
        via: flightData.via,
        price: flightData.price || 0,
        discount: flightData.discount,
        arrivalAirport: flightData.arrivalAirport,
        departureAirport: flightData.departureAirport,
        isNbDeparture: flightData.isNbDeparture,
        isNbArrival: flightData.isNbArrival,
        layovers: getLayoversForFlight(flight, sectors),
        seatsLeft: (flight as FlightCard).indicators?.seatsLeft,
        noBaggage: (flight as FlightCard).indicators?.noBaggage,
        longLayover: (flight as FlightCard).indicators?.longLayover,
        shortLayover: (flight as FlightCard).indicators?.shortLayover,
        strikeOffPrice: isPerPaxPricingEnabled
            ? (flight as FlightCard).priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice
            : (flight as FlightCard).priceBreakup?.fare?.strike_off_details?.strikedOffPrice,
        coupon_detail: (flight as FlightCard).priceBreakup?.fare?.coupon_detail,
        arrivalDate: (flight as FlightCard)?.lastArrival?.date || '',
        departureDate: (flight as FlightCard)?.firstDeparture?.date || '',
        transitVisa: Boolean((flight as FlightCard)?.tv),
        // Already passed through by ui-middle-end's _filterFareObj keysToPick.
        supplierName: (flight as FlightCard).priceBreakup?.fare?.supplierName
    }
}

// Generic getFlightData utility for both desktop and mobile
interface GetFlightDataProps {
    flight: FlightCard
    airline_names: Record<string, string>
    sectors: Sectors
    isRtSolution?: boolean
    isPerPaxPricingEnabled?: boolean
    translationsFn: any
}

export interface GetFlightDataReturn {
    airlineName: string
    departureTime: string
    arrivalTime: string
    stops: number
    airlineCodes: string[]
    totalDuration: number
    via: string
    price: number
    discount: number
    arrivalAirport: string
    departureAirport: string
    isNbDeparture?: boolean
    isNbArrival?: boolean
    strikeOffPrice?: number
    promos?: PromoData[]
}
export function getFlightData ({
    flight,
    airline_names,
    sectors,
    isRtSolution,
    translationsFn,
    isPerPaxPricingEnabled
}: GetFlightDataProps): GetFlightDataReturn {
    const { airlineCodes = [], sectorKeys, priceBreakup, totalDuration, nbAirports } = flight || {}

    const airlineName =
        airlineCodes.length > 1 ? translationsFn?.('multipleAirlines') : airline_names[airlineCodes[0]] || ''
    let via = ''
    const sector = sectors[sectorKeys?.[0]] || {}
    const {
        stops,
        totalDuration: durationObj = { hh: 0, mm: 0 },
        flights: { segments = [] } = {},
        firstDeparture,
        lastArrival
    } = sector
    const departureTime = firstDeparture?.time || ''
    const arrivalTime = lastArrival?.time || ''
    const departureAirport = firstDeparture?.airportCode || ''
    const arrivalAirport = lastArrival?.airportCode || ''
    const isNbDeparture = nbAirports && nbAirports.includes(departureAirport)
    const isNbArrival = nbAirports && nbAirports.includes(arrivalAirport)
    if (segments.length > 1) {
        const viaSegments: string[] = []
        segments.forEach(segment => {
            const { arrival } = segment
            viaSegments.push(arrival.airportCode)
        })
        via = viaSegments.slice(0, -1).join(' - ')
    }
    const price = isRtSolution
        ? priceBreakup?.multiAirlinePrice!
        : isPerPaxPricingEnabled
            ? priceBreakup?.totalAdultFare!
            : priceBreakup?.pr || 0
    const strikeOffPrice = isRtSolution ? priceBreakup?.multiAirlinePriceWoDiscount! : undefined
    const discount = priceBreakup?.ds
    const promos = flight?.promos || []
    return {
        airlineName,
        departureTime,
        arrivalTime,
        stops,
        airlineCodes,
        totalDuration,
        via,
        price,
        discount,
        arrivalAirport,
        departureAirport,
        isNbDeparture,
        isNbArrival,
        strikeOffPrice,
        promos
    }
}

// ---- Multicity ----

export interface MulticityJourney {
    sectorIndex: number
    airlineName: string
    airlineCodes: string[]
    departureTime: string
    arrivalTime: string
    departureAirport: string
    arrivalAirport: string

    departureDate: string
    arrivalDate: string
    stops: number
    via: string
    durationSeconds: number
    layovers: Array<{ durationMins: number; durationStr: string; city: string }>
    isNbDeparture?: boolean
    isNbArrival?: boolean
}

export interface MulticityFlightData {
    journeys: MulticityJourney[]
    price: number
    strikeOffPrice?: number
    discount: string
    seatsLeft?: number
    noBaggage?: boolean
    coupon_detail?: CouponDetails
    promos: PromoData[]
    transitVisa?: boolean
}

interface GetMulticityFlightDataProps {
    flight: FlightCard
    airline_names: Record<string, string>
    sectors: Sectors
    translationsFn: any
    isPerPaxPricingEnabled?: boolean
}

/**
 * Builds per-journey display rows for a multicity card. Each entry in `sectorKeys`
 * is one journey; price is the combined value for the whole itinerary.
 */
export function getMulticityFlightData ({
    flight,
    airline_names,
    sectors,
    translationsFn,
    isPerPaxPricingEnabled
}: GetMulticityFlightDataProps): MulticityFlightData {
    const { sectorKeys = [], priceBreakup, tripDurationMapBySector = [], indicators } = flight || {}

    const journeys: MulticityJourney[] = sectorKeys.map((key, index) => {
        const sector = sectors?.[key] || ({} as Sectors[string])
        const {
            stops = 0,
            totalDuration: durationObj = { hh: 0, mm: 0 },
            flights: { segments = [] } = {},
            firstDeparture,
            lastArrival,
            layoverDurationMap = []
        } = sector

        const journeyAirlineCodes = Array.from(
            new Set(segments.map(segment => segment?.ac).filter((code): code is string => !!code))
        )
        const airlineName =
            journeyAirlineCodes.length > 1
                ? translationsFn?.('multipleAirlines')
                : airline_names[journeyAirlineCodes[0]] || ''

        let via = ''
        if (segments.length > 1) {
            via = segments
                .map(segment => segment.arrival.airportCode)
                .slice(0, -1)
                .join(' - ')
        }

        const layovers: Array<{ durationMins: number; durationStr: string; city: string }> = []
            ; (layoverDurationMap as number[]).forEach((layoverMins, idx) => {
                if (!segments[idx] || !segments[idx + 1]) return
                const city = segments[idx].arrival?.airportName?.c || segments[idx].arrival?.airportCode || ''
                layovers.push({
                    durationMins: layoverMins,
                    durationStr: `${Math.floor(layoverMins / 60)}h ${layoverMins % 60}m`,
                    city
                })
            })

        const durationMinutes =
            typeof tripDurationMapBySector?.[index] === 'number'
                ? tripDurationMapBySector[index]
                : (durationObj?.hh || 0) * 60 + (durationObj?.mm || 0)

        const departureAirport = firstDeparture?.airportCode || ''
        const arrivalAirport = lastArrival?.airportCode || ''
        const isNbDeparture = sector.nbAirports ? sector.nbAirports.includes(departureAirport) : false
        const isNbArrival = sector.nbAirports ? sector.nbAirports.includes(arrivalAirport) : false

        return {
            sectorIndex: index,
            airlineName,
            airlineCodes: journeyAirlineCodes,
            departureTime: firstDeparture?.time || '',
            arrivalTime: lastArrival?.time || '',
            departureAirport,
            arrivalAirport,
            departureDate: firstDeparture?.date || '',
            arrivalDate: lastArrival?.date || '',
            stops,
            via,
            durationSeconds: durationMinutes * 60,
            layovers,
            isNbDeparture,
            isNbArrival
        }
    })

    const price = isPerPaxPricingEnabled ? priceBreakup?.totalAdultFare ?? priceBreakup?.pr ?? 0 : priceBreakup?.pr || 0

    return {
        journeys,
        price,
        strikeOffPrice: isPerPaxPricingEnabled
            ? priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice
            : priceBreakup?.fare?.strike_off_details?.strikedOffPrice,
        discount: priceBreakup?.ds ? `₹${priceBreakup.ds.toLocaleString()}` : '',
        seatsLeft: indicators?.seatsLeft,
        noBaggage: indicators?.noBaggage,
        coupon_detail: priceBreakup?.fare?.coupon_detail as CouponDetails | undefined,
        promos: flight?.promos || [],
        transitVisa: Boolean(flight?.tv)
    }
}
